'use client'

import { useEffect, useRef, useState } from 'react'
import { HUB_CHROME, UNITY_DASHBOARD, UNITY_HUB, UNITY_SUITE } from './suite.js'
import { AppMark, HubMark } from './AppMarks.jsx'
import { isUnityEmbedded } from './detect.js'
import { UnityProfileMenu } from './UnityProfileMenu.jsx'
import './unity-chrome.css'

/**
 * Common Unity Cosmos header for ecosystem landings/dashboards.
 * @param {{ currentId?: string, showAppsMenu?: boolean, hubHref?: string, lang?: 'en' | 'es', children?: import('react').ReactNode }} props
 */
export function UnityEcosystemBar({
  currentId = 'nightsky',
  showAppsMenu = true,
  hubHref = UNITY_HUB,
  lang = 'es',
  menuSlot = null,
  surfaceMode = 'dark',
  onLogin = null,
  onLogout = null,
  showLogout = true,
  children = null,
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const current =
    currentId === 'hub'
      ? HUB_CHROME
      : UNITY_SUITE.find((a) => a.id === currentId) || UNITY_SUITE[0]
  const theme = current.theme || {}

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (isUnityEmbedded()) return null

  return (
    <header
      className={`uc-chrome uc-chrome--${current.id}`}
      data-world={current.id}
      ref={rootRef}
      style={{
        ['--uc-accent']: current.accent,
        ['--uc-bg']: theme.bg || '#0a0c12',
        ['--uc-line']: theme.line || 'rgba(232, 236, 242, 0.1)',
        ['--uc-ink']: theme.ink || '#eef1f6',
        ['--uc-muted']: theme.muted || 'rgba(168, 175, 188, 0.95)',
        ['--uc-surface']: theme.surface || 'rgba(255, 255, 255, 0.03)',
        ['--uc-surface-border']: theme.surfaceBorder || 'rgba(232, 236, 242, 0.14)',
        ['--uc-cta-ink']: theme.ctaInk || '#0a0c12',
        ['--uc-cosmos']: theme.cosmos || '#33ccff',
      }}
    >
      <div className="uc-chrome__lead">
        <a className="uc-chrome__brand" href={hubHref} aria-label="Unity Cosmos home">
          <span className="uc-chrome__mark" aria-hidden>
            <HubMark accent={current.accent} size={26} />
          </span>
        </a>

        {showAppsMenu ? (
          <div className="uc-chrome__world">
            <button
              type="button"
              className="uc-chrome__world-btn"
              aria-expanded={open}
              aria-haspopup="listbox"
              onClick={() => setOpen((v) => !v)}
            >
              <span className="uc-chrome__world-dot" aria-hidden />
              <span className="uc-chrome__world-copy">
                <span className="uc-chrome__world-label">World</span>
                <span className="uc-chrome__world-name">{current.name}</span>
              </span>
              <span className={`uc-chrome__world-chev${open ? ' is-open' : ''}`} aria-hidden>
                ▾
              </span>
            </button>
            {open ? (
              <div className="uc-chrome__menu-wrap">
                <div className="uc-chrome__menu-head">
                  <span className="uc-chrome__menu-kicker">Worlds</span>
                  <span className="uc-chrome__menu-count">{UNITY_SUITE.length} live</span>
                </div>
                <ul className="uc-chrome__menu" role="listbox" aria-label="Unity Cosmos worlds">
                  {UNITY_SUITE.map((app) => {
                    const selected = app.id === currentId
                    const live = app.status === 'live'
                    const targetHref = selected ? '/' : (live ? `${UNITY_DASHBOARD}?world=${app.id}` : UNITY_HUB)
                    return (
                      <li key={app.id}>
                        <a
                          href={targetHref}
                          role="option"
                          aria-selected={selected}
                          className={[
                            'uc-chrome__menu-item',
                            selected ? 'is-selected' : '',
                            live ? 'is-live' : 'is-soon',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={{ ['--item-accent']: app.accent }}
                          onClick={() => setOpen(false)}
                        >
                          <span className="uc-chrome__menu-glyph" aria-hidden>
                            <AppMark id={app.id} accent={app.accent} size={28} />
                          </span>
                          <span className="uc-chrome__menu-copy">
                            <span className="uc-chrome__menu-name">{app.name}</span>
                          </span>
                          <span className="uc-chrome__menu-status">
                            <span className="uc-chrome__menu-pulse" aria-hidden />
                            {live ? 'Live' : 'Soon'}
                          </span>
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="uc-chrome__actions">
        {children}
        {menuSlot}
        <UnityProfileMenu lang={lang} onLogin={onLogin} onLogout={onLogout} />
      </div>
    </header>
  )
}

export { isUnityEmbedded } from './detect.js'
